import React, { useState, useContext } from 'react';
import Errors from '../common/Errors';
import Row from '../common/Row';
import { MarkedEditor } from '../common/Marked';
import { ContextMenu } from '../common/ContextMenu';
import Select from '../common/Select';
import MailerFieldChooserModal from './mailer-body-inject/MailerFieldChooserModal';
import { MailerContext } from '../../hooks/Contexts';
import { useForm, useFormState } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import {
  defaultChooserState,
  previewTokenNames,
} from './constants/mailerConstants';
import { handleUpdateMailer } from './constants/handleMailer';
import { InvalidBodyFields } from './mailer-body-inject/InvalidBodyFields';
import Icon from '../common/Icon';

const MailerEdit = ({ toggleEdit }) => {
  const context = useContext(MailerContext);
  const {
    form,
    fieldsets,
    metadata_entries,
    mailer,
    setMailer,
    invalidIds,
    mailerInvalid,
    related_forms,
  } = context;
  const { field_id } = mailer;
  const { id, updated_at, created_at, form_id, ...editableFields } = mailer;
  const tokenizableInputs = ['subject', 'from', 'body', 'pdf_body'];
  const defaultForm = { form, fieldsets, metadata_entries };
  const [chooserState, setChooserState] = useState({
    selectedForm: defaultForm,
    ...defaultChooserState,
  });

  const [isInjecting, setIsInjecting] = useState(false);
  const [insertionPoint, setInsertionPoint] = useState({
    position: 0,
    name: '',
  });
  const [selectableEntries, setSelectableEntries] = useState(
    metadata_entries.filter((me) => me.field_id == field_id)
  );
  const {
    register,
    handleSubmit,
    control,
    setValue,
    getValues,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues: { ...editableFields },
    criteriaMode: 'all',
    shouldUnregister: true,
  });
  const { isDirty, dirtyFields } = useFormState({ control });
  const onSubmit = async (formData) => {
    if (isDirty) {
      if (!formData.metadata_entry_id) {
        formData.metadata_entry_id = null;
      }
      let updated = await handleUpdateMailer(mailer.id, formData);
      if (updated.ok) {
        setMailer(updated.data);
        toggleEdit();
      } else if (updated.errors) {
        Object.entries(updated.errors).forEach(([key, message]) =>
          setError(key, { types: { [key]: message } })
        );
      }
    } else {
      toggleEdit();
    }
  };

  const toggleTipElement = (e, elementId, show) => {
    e.preventDefault();
    let el = document.getElementById(elementId);
    el.style.display = show ? 'block' : 'none';
  };
  const defaultTipStyle = {
    display: 'none',
    fontSize: '14px',
    marginLeft: '6px',
  };

  function toFieldChanged(fieldId) {
    let entries = metadata_entries.filter((me) => me.field_id == fieldId);
    setValue('metadata_entry_id', null);
    setSelectableEntries(entries);
  }

  function setupInject(mailerBodyType, chosenForm) {
    let el = document.getElementsByName(mailerBodyType);
    setChooserState({ selectedForm: chosenForm, ...defaultChooserState });
    setIsInjecting(true);
    setInsertionPoint({ position: el[0].selectionStart, name: mailerBodyType });
  }

  function insertAttr(category, attr, id, relatedFormID = null) {
    let body = getValues(insertionPoint.name);
    let n = insertionPoint.position;

    if (category === 'custom') {
      let type = insertionPoint.name == 'body' ? 'body' : 'pdf';
      let customCount = 0;
      customCount = (body.match(/{{custom./g) || []).length;
      attr = type + '.' + (customCount + 1).toString();
    } else {
      category = id ? category + `.${id}` : category;
      category = relatedFormID
        ? `related_form.${relatedFormID}.` + category
        : category;
      if (attr.indexOf('Meta: ') != -1) {
        // Remove 'Meta: ' when inserting metadata token
        attr = attr.substr(6);
        if (
          category == 'form' ||
          (relatedFormID && category == `related_form.${relatedFormID}.form`)
        ) {
          // Form metadata requires 'meta.'
          attr = 'meta.' + attr;
        }
      }
    }

    let formattedAttr = '{{' + category + `.${attr}` + '}}';
    body = body.slice(0, n) + formattedAttr + body.slice(n);

    setValue(insertionPoint.name, body, { shouldDirty: true });
    setInsertionPoint((state) => {
      return { ...state, position: n + formattedAttr.length };
    });
  }

  function insertChildAttr(token) {
    let body = getValues(insertionPoint.name);
    let n = insertionPoint.position;
    body = body.slice(0, n) + token + body.slice(n);
    setValue(insertionPoint.name, body, { shouldDirty: true });
    setInsertionPoint((state) => {
      return { ...state, position: n + token.length };
    });
  }

  const contextMenuOptions = (target) => (
    <>
      <li
        key={`default-${target}`}
        onClick={(e) => setupInject(target, defaultForm)}
      >
        Insert Field
      </li>
      {related_forms && (
        <>
          {related_forms
            .filter((f) => f.form.is_parent)
            .map((f) => (
              <li
                key={`parent-${target}`}
                onClick={(e) => setupInject(target, f)}
              >
                Insert Parent Fields: {f.form.name}
              </li>
            ))}
          {related_forms
            .filter((f) => f.form.submission_validate)
            .map((f) => (
              <li
                key={`child-${target}-${f.form.id}`}
                onClick={(e) => setupInject(target, f)}
              >
                Insert Child Fields: {f.form.name}
              </li>
            ))}
        </>
      )}
    </>
  );

  return (
    <>
      <MailerFieldChooserModal
        insertAttr={insertAttr}
        defaultForm={defaultForm}
        chooserState={chooserState}
        setChooserState={setChooserState}
        isInjecting={isInjecting}
        setIsInjecting={setIsInjecting}
        insertChildAttr={insertChildAttr}
      />
      {tokenizableInputs.map((property) => (
        <ContextMenu
          target={property}
          menuOptions={contextMenuOptions(property)}
          key={`context-menu-${property}`}
        />
      ))}

      <div className='details'>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Row name='To Field value:'>
            <div className='select-input'>
              <select
                {...register('field_id', {
                  required: 'Required',
                  onChange: (e) => toFieldChanged(e.target.value),
                })}
              >
                <option value=''>Select Field...</option>
                {fieldsets.map((fs, i) => {
                  return (
                    <optgroup key={i} label={fs.fieldset.name}>
                      {fs.fields.map((f, i) => {
                        return (
                          <option
                            value={f.id}
                            key={f.id}
                            disabled={!['text', 'select'].includes(f.type)}
                          >
                            {f.name}
                          </option>
                        );
                      })}
                    </optgroup>
                  );
                })}
              </select>
            </div>
            <ErrorMessage
              errors={errors}
              name='field_id'
              render={({ messages }) => <Errors messages={messages} />}
            />
          </Row>

          {selectableEntries.length > 0 && (
            <Row name='To Meta Entry Value:'>
              <Select
                content={[
                  { label: '---', value: null },
                  ...selectableEntries.map((me) => {
                    return { label: me.title, value: me.id };
                  }),
                ]}
                register={{
                  ...register('metadata_entry_id'),
                }}
              />
            </Row>
          )}

          <Row name='Name:'>
            <input {...register('name', { required: 'Required' })} />
            <ErrorMessage
              errors={errors}
              name='name'
              render={({ messages }) => <Errors messages={messages} />}
            />
          </Row>

          <Row name='On Submit:'>
            <input id='on_submit' type='checkbox' {...register('on_submit')} />
            <label htmlFor='on_submit' />
          </Row>

          <Row name='Allow Universal Sending?'>
            <input
              id='universal'
              type='checkbox'
              {...register('universal_mail_send')}
            />
            <label htmlFor='universal' />
          </Row>

          <Row name='From:'>
            <input
              {...register('from')}
              onFocus={(e) => toggleTipElement(e, 'from-tip', true)}
              onBlur={(e) => toggleTipElement(e, 'from-tip', false)}
            />
            <i id='from-tip' style={defaultTipStyle}>
              Shift + Click to insert data
            </i>
            <ErrorMessage
              errors={errors}
              name='from'
              render={({ messages }) => <Errors messages={messages} />}
            />
          </Row>

          <Row name='Subject:'>
            <input
              {...register('subject', { required: 'Required' })}
              onFocus={(e) => toggleTipElement(e, 'subject-tip', true)}
              onBlur={(e) => toggleTipElement(e, 'subject-tip', false)}
            />
            <i id='subject-tip' style={defaultTipStyle}>
              Shift + Click to insert data
            </i>
            <ErrorMessage
              errors={errors}
              name='subject'
              render={({ messages }) => <Errors messages={messages} />}
            />
          </Row>

          <Row name='Calendar Field:'>
            <div className='select-input'>
              <select {...register('calendar_event_field_id')}>
                <option value=''>None...</option>
                {fieldsets.map((fs, i) => {
                  return (
                    <optgroup
                      key={`fs-${fs.fieldset.id}-cal`}
                      label={fs.fieldset.name}
                    >
                      {fs.fields.map((f, i) => {
                        return (
                          <option
                            value={f.id}
                            key={`${f.id}-cal`}
                            disabled={!['radio', 'select'].includes(f.type)}
                          >
                            {f.name}
                          </option>
                        );
                      })}
                    </optgroup>
                  );
                })}
              </select>
            </div>
            <ErrorMessage
              errors={errors}
              name='calendar_event_field_id'
              render={({ messages }) => <Errors messages={messages} />}
            />
          </Row>

          {mailerInvalid && <InvalidBodyFields invalidIds={invalidIds} />}

          <Row name='Body:'>
            <MarkedEditor
              getValues={() =>
                previewTokenNames(getValues('body'), fieldsets, related_forms)
              }
              register={{ ...register('body', { required: 'Required' }) }}
              onFocus={(e) => toggleTipElement(e, 'body-tip', true)}
              onBlur={(e) => toggleTipElement(e, 'body-tip', false)}
            />
            <i id='body-tip' style={defaultTipStyle}>
              Shift + Click to insert data
            </i>
            <ErrorMessage
              errors={errors}
              name='body'
              render={({ messages }) => <Errors messages={messages} />}
            />
          </Row>

          <Row name='PDF Body:'>
            <MarkedEditor
              getValues={() =>
                previewTokenNames(
                  getValues('pdf_body'),
                  fieldsets,
                  related_forms
                )
              }
              register={{ ...register('pdf_body') }}
              onFocus={(e) => toggleTipElement(e, 'pdf_body-tip', true)}
              onBlur={(e) => toggleTipElement(e, 'pdf_body-tip', false)}
            />
            <i id='pdf_body-tip' style={defaultTipStyle}>
              Shift + Click to insert data
            </i>
          </Row>

          <Row name='Landscape:'>
            <input id='landscape' type='checkbox' {...register('landscape')} />
            <label htmlFor='landscape' />
          </Row>

          <div className='row'>
            <div className='controls lower'>
              <button type='submit'>
                Save
                <Icon icon='save' />
              </button>
              <button onClick={toggleEdit} className='blue'>
                Cancel
                <Icon icon='xmark' />
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default MailerEdit;
